import get from 'lodash/get'
import flatMap from 'lodash/flatMap'
import cloneDeep from 'lodash/cloneDeep'

import {
  fetchCollection,
  fetchResource,
  createResource,
  updateResource,
  deleteResource,
  performActionOnResource
} from './index'

import { arrayOf } from 'normalizr'

import { servicingSchema } from '../schemas'

import imageToDataURI from '../helpers/imageToDataURI'


export const SERVICINGS_REQUEST = 'SERVICINGS_REQUEST'
export const SERVICINGS_SUCCESS = 'SERVICINGS_SUCCESS'
export const SERVICINGS_FAILURE = 'SERVICINGS_FAILURE'

export function fetchServicingsForWorkOrder (url, type, workOrderId, reload) {
  return fetchCollection({
    url,
    key: 'servicings',
    schema: servicingSchema,
    actions: [ SERVICINGS_REQUEST, SERVICINGS_SUCCESS, SERVICINGS_FAILURE ],
    responseInterceptor: response => ({ ...response, type, workOrderId, reload })
  })
}

export function loadServicingsForWorkOrder (workOrderId, type, nextPage) {
  const types = [ 'finished', 'unfinished', 'finished_with_deleted' ]

  if (!types.includes(type)) {
    throw new Error('`type` must be one of ' + types.join(', '))
  }

  return (dispatch, getState) => {
    const workOrder = get(getState(), `workOrders.${workOrderId}`, {})
    let actionName = type === 'finished' && workOrder.showInputted && workOrder.status === 'signed_off' ? 'finished_with_deleted' : type
    let {
      nextPageUrl
    } = get(getState(), `pagination.servicingsByWorkOrder.${workOrderId}-${actionName}`, {})

    if (!nextPage) {
      nextPageUrl = `/work_orders/${workOrderId}/${actionName}_servicings`
    }

    return dispatch(fetchServicingsForWorkOrder(nextPageUrl, actionName, workOrderId, !nextPage))
  }
}

export const SERVICING_REQUEST = 'SERVICING_REQUEST'
export const SERVICING_SUCCESS = 'SERVICING_SUCCESS'
export const SERVICING_FAILURE = 'SERVICING_FAILURE'

export function fetchServicing (id) {
  return fetchResource({
    url: `/servicings/${id}`,
    key: 'servicing',
    schema: servicingSchema,
    actions: [ SERVICING_REQUEST, SERVICING_SUCCESS, SERVICING_FAILURE ]
  })
}

export const UPDATE_SERVICING_REQUEST = 'UPDATE_SERVICING_REQUEST'
export const UPDATE_SERVICING_SUCCESS = 'UPDATE_SERVICING_SUCCESS'
export const UPDATE_SERVICING_FAILURE = 'UPDATE_SERVICING_FAILURE'

export function updateServicing (servicingId, _data) {
  return (dispatch) => encodeImages(_data).then((data) => {
    return dispatch(
      updateResource({
        url: `/servicings/${servicingId}`,
        key: 'servicing',
        schema: servicingSchema,
        data,
        actions: [ UPDATE_SERVICING_REQUEST, UPDATE_SERVICING_SUCCESS, UPDATE_SERVICING_FAILURE ]
      })
    )
  })
  .catch(console.log)
}

export const ADD_SERVICING_SERVICE_REQUEST = 'ADD_SERVICING_SERVICE_REQUEST'
export const ADD_SERVICING_SERVICE_SUCCESS = 'ADD_SERVICING_SERVICE_SUCCESS'
export const ADD_SERVICING_SERVICE_FAILURE = 'ADD_SERVICING_SERVICE_FAILURE'

export function addServices (servicingId, data) {
  return performActionOnResource({
    url: `/servicings/${servicingId}`,
    key: 'servicing',
    action: 'add_services',
    schema: servicingSchema,
    body: data,
    actions: [ADD_SERVICING_SERVICE_REQUEST, ADD_SERVICING_SERVICE_SUCCESS, ADD_SERVICING_SERVICE_FAILURE]
  })
}

export const MARK_SERVICING_OPEN_REQUEST = 'MARK_SERVICING_OPEN_REQUEST'
export const MARK_SERVICING_OPEN_SUCCESS = 'MARK_SERVICING_OPEN_SUCCESS'
export const MARK_SERVICING_OPEN_FAILURE = 'MARK_SERVICING_OPEN_FAILURE'

export function markServicingAsOpen (servicingId, data) {
  return performActionOnResource({
    url: `/servicings/${servicingId}`,
    key: 'servicing',
    action: 'mark_as_open',
    schema: servicingSchema,
    body: data,
    actions: [ MARK_SERVICING_OPEN_REQUEST, MARK_SERVICING_OPEN_SUCCESS, MARK_SERVICING_OPEN_FAILURE ]
  })
}

export const CREATE_SERVICING_REQUEST = 'CREATE_SERVICING_REQUEST'
export const CREATE_SERVICING_SUCCESS = 'CREATE_SERVICING_SUCCESS'
export const CREATE_SERVICING_FAILURE = 'CREATE_SERVICING_FAILURE'

export function createServicingForWorkOrder (workOrderId, data) {
  return createResource({
    url: `/work_orders/${workOrderId}/servicings`,
    key: 'servicing',
    schema: servicingSchema,
    data,
    actions: [ CREATE_SERVICING_REQUEST, CREATE_SERVICING_SUCCESS, CREATE_SERVICING_FAILURE ],
    responseInterceptor: response => ({ ...response, type: 'unfinished', workOrderId })
  })
}

export const DELETE_SERVICING_REQUEST = 'DELETE_SERVICING_REQUEST'
export const DELETE_SERVICING_SUCCESS = 'DELETE_SERVICING_SUCCESS'
export const DELETE_SERVICING_FAILURE = 'DELETE_SERVICING_FAILURE'

export function deleteServicing (servicingId) {
  return deleteResource({
    url: `/servicings/${servicingId}`,
    actionPayload: { resourceId: servicingId },
    actions: [ DELETE_SERVICING_REQUEST, DELETE_SERVICING_SUCCESS, DELETE_SERVICING_FAILURE ]
  })
}

export const ADD_HIGH_PRIORITY_REQUEST = 'ADD_HIGH_PRIORITY_REQUEST'
export const ADD_HIGH_PRIORITY_SUCCESS = 'ADD_HIGH_PRIORITY_SUCCESS'
export const ADD_HIGH_PRIORITY_FAILURE = 'ADD_HIGH_PRIORITY_FAILURE'

export function markServicingAsHighPriority (servicingId) {
  return performActionOnResource({
    url: `/servicings/${servicingId}`,
    action: 'mark_as_high_priority',
    key: 'servicing',
    schema: servicingSchema,
    actions: [ ADD_HIGH_PRIORITY_REQUEST, ADD_HIGH_PRIORITY_SUCCESS, ADD_HIGH_PRIORITY_FAILURE ]
  })
}

export const REMOVE_HIGH_PRIORITY_REQUEST = 'REMOVE_HIGH_PRIORITY_REQUEST'
export const REMOVE_HIGH_PRIORITY_SUCCESS = 'REMOVE_HIGH_PRIORITY_SUCCESS'
export const REMOVE_HIGH_PRIORITY_FAILURE = 'REMOVE_HIGH_PRIORITY_FAILURE'

export function markServicingAsLowPriority (servicingId) {
  return performActionOnResource({
    url: `/servicings/${servicingId}`,
    action: 'mark_as_low_priority',
    key: 'servicing',
    schema: servicingSchema,
    actions: [ REMOVE_HIGH_PRIORITY_REQUEST, REMOVE_HIGH_PRIORITY_SUCCESS, REMOVE_HIGH_PRIORITY_FAILURE ]
  })
}

export const TOGGLE_REQUIRE_IMAGES_REQUEST = 'TOGGLE_REQUIRE_IMAGES_REQUEST'
export const TOGGLE_REQUIRE_IMAGES_SUCCESS = 'TOGGLE_REQUIRE_IMAGES_SUCCESS'
export const TOGGLE_REQUIRE_IMAGES_FAILURE = 'TOGGLE_REQUIRE_IMAGES_FAILURE'

export function toggleRequireImages (servicingId) {
  return performActionOnResource({
    url: `/servicings/${servicingId}`,
    action: 'toggle_require_images',
    key: 'servicing',
    schema: servicingSchema,
    actions: [ TOGGLE_REQUIRE_IMAGES_REQUEST, TOGGLE_REQUIRE_IMAGES_SUCCESS, TOGGLE_REQUIRE_IMAGES_FAILURE ]
  })
}

export const CLAIM_SERVICING_REQUEST = 'CLAIM_SERVICING_REQUEST'
export const CLAIM_SERVICING_SUCCESS = 'CLAIM_SERVICING_SUCCESS'
export const CLAIM_SERVICING_FAILURE = 'CLAIM_SERVICING_FAILURE'

export function claimServicing (servicingId) {
  return performActionOnResource({
    url: `/servicings/${servicingId}`,
    action: 'claim',
    key: 'servicing',
    schema: servicingSchema,
    actions: [ CLAIM_SERVICING_REQUEST, CLAIM_SERVICING_SUCCESS, CLAIM_SERVICING_FAILURE ]
  })
}

export const ASSIGN_SERVICING_REQUEST = 'ASSIGN_SERVICING_REQUEST'
export const ASSIGN_SERVICING_SUCCESS = 'ASSIGN_SERVICING_SUCCESS'
export const ASSIGN_SERVICING_FAILURE = 'ASSIGN_SERVICING_FAILURE'

export function assignServicing (servicingId, technicianId) {
  return performActionOnResource({
    url: `/servicings/${servicingId}`,
    action: 'assign',
    key: 'servicing',
    body: { technician_id: technicianId },
    schema: servicingSchema,
    actions: [ ASSIGN_SERVICING_REQUEST, ASSIGN_SERVICING_SUCCESS, ASSIGN_SERVICING_FAILURE ]
  })
}

export const APPROVE_SERVICING_REQUEST = 'APPROVE_SERVICING_REQUEST'
export const APPROVE_SERVICING_SUCCESS = 'APPROVE_SERVICING_SUCCESS'
export const APPROVE_SERVICING_FAILURE = 'APPROVE_SERVICING_FAILURE'

export function approveServicing (servicingId) {
  return performActionOnResource({
    url: `/servicings/${servicingId}`,
    action: 'approve',
    key: 'servicing',
    schema: servicingSchema,
    actions: [ APPROVE_SERVICING_REQUEST, APPROVE_SERVICING_SUCCESS, APPROVE_SERVICING_FAILURE ]
  })
}

export const APPROVE_REMAINING_SERVICINGS_REQUEST = 'APPROVE_REMAINING_SERVICINGS_REQUEST'
export const APPROVE_REMAINING_SERVICINGS_SUCCESS = 'APPROVE_REMAINING_SERVICINGS_SUCCESS'
export const APPROVE_REMAINING_SERVICINGS_FAILURE = 'APPROVE_REMAINING_SERVICINGS_FAILURE'

export function approveRemainingServicings (workOrderId) {
  return performActionOnResource({
    url: `/work_orders/${workOrderId}/servicings`,
    action: 'approve_remaining',
    key: 'servicings',
    schema: arrayOf(servicingSchema),
    actions: [
      APPROVE_REMAINING_SERVICINGS_REQUEST,
      APPROVE_REMAINING_SERVICINGS_SUCCESS,
      APPROVE_REMAINING_SERVICINGS_FAILURE
    ]
  })
}

export const DENY_SERVICING_REQUEST = 'DENY_SERVICING_REQUEST'
export const DENY_SERVICING_SUCCESS = 'DENY_SERVICING_SUCCESS'
export const DENY_SERVICING_FAILURE = 'DENY_SERVICING_FAILURE'

export function denyServicing (servicingId) {
  return performActionOnResource({
    url: `/servicings/${servicingId}`,
    action: 'deny',
    key: 'servicing',
    schema: servicingSchema,
    actions: [ DENY_SERVICING_REQUEST, DENY_SERVICING_SUCCESS, DENY_SERVICING_FAILURE ]
  })
}

export const RESCHEDULE_SERVICING_REQUEST = 'RESCHEDULE_SERVICING_REQUEST'
export const RESCHEDULE_SERVICING_SUCCESS = 'RESCHEDULE_SERVICING_SUCCESS'
export const RESCHEDULE_SERVICING_FAILURE = 'RESCHEDULE_SERVICING_FAILURE'

export function rescheduleServicing (servicingId, data) {
  return performActionOnResource({
    url: `/servicings/${servicingId}`,
    action: 'reschedule',
    key: 'servicing',
    schema: servicingSchema,
    body: data,
    actions: [ RESCHEDULE_SERVICING_REQUEST, RESCHEDULE_SERVICING_SUCCESS, RESCHEDULE_SERVICING_FAILURE ]
  })
}

export const REMOVE_MERGED_SERVICES_FOR_SERVICING = 'REMOVE_MERGED_SERVICES_FOR_SERVICING'

export function removeMergedServices (servicingId) {
  return {
    type: REMOVE_MERGED_SERVICES_FOR_SERVICING,
    servicingId
  }
}

function encodeImages (data) {
  if (!data.beforeAftersAttributes || data.beforeAftersAttributes.length === 0) return Promise.resolve(data)

  // if there are images, parse each pair into a base64 encoded string and filter the 
  return Promise.all(
    flatMap(data.beforeAftersAttributes, ba => [imageToDataURI(ba.beforeImage), imageToDataURI(ba.afterImage)])
  ).then((encodedBeforeAfters) => {
   const newData = cloneDeep(data)
    newData.beforeAftersAttributes = newData.beforeAftersAttributes.map(elm => {
      const [beforeImage, afterImage] = encodedBeforeAfters.splice(0, 2)
      // If a new image file was selected, use the encoded base64 string
      // If the image was removed/cleared, send null
      if (beforeImage) {
        elm.beforeImage = beforeImage
      } else if (!elm.beforeImage || !elm.beforeImage.preview) {
        elm.beforeImage = null
      }
      if (afterImage) {
        elm.afterImage = afterImage
      } else if (!elm.afterImage || !elm.afterImage.preview) {
        elm.afterImage = null
      }
      return elm
     }).filter(elm => elm.id || elm.beforeImage || elm.afterImage)

    return newData
  })
}



// WEBPACK FOOTER //
// ./src/actions/servicings.js